import React, { useState, useEffect } from 'react';
import { UserCircle, Check } from '@phosphor-icons/react';
import classNames from 'classnames';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  trackVBAssignModalAddAssignee,
  trackVBAssignModalRemoveAssignee,
  trackVBAssignModalChangeAssess,
  trackVBAssignModalOpenLink,
  trackVBAssignModalCopyLink,
  trackVBAssignModalSaveSuccess,
  trackVBAssignModalSaveError,
} from '../../../../analytics';

import { LinkBox } from '../../../../components/custom';
import { Form, FloatingLabel, FormError } from '../../../../components/form';
import {
  Button,
  Modal,
  Dropdown,
  Checkbox,
} from '../../../../components/general';

import { URL, EMAILS } from '../../../../constants';

import useAuthUserStore from '../../../../hooks/useAuthUserStore';

import { sendEmail } from '../../../../services/api/emails';
import { updateEvent } from '../../../../services/api/events';

import { useProjectStore } from '../../../../stores/project';

import { AssigneeType } from '../../../../types';
import { isEmpty, validateEmail } from '../../../../utils';

type AccessFlag = 'isPeopleChecked' | 'isHlReelChecked' | 'isClipsChecked';

interface Assignee {
  email: string;
  access: string;
  isPeopleChecked?: boolean;
  isHlReelChecked?: boolean;
  isClipsChecked?: boolean;
}

interface Props {
  show: boolean;
  onHide: () => void;
}

const AssignModal: React.FC<Props> = (props) => {
  const { show, onHide } = props;

  const { user } = useAuthUserStore();
  const { currentProject, updateCurrentEvent } = useProjectStore();

  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  // fill assignee array based on data from database
  useEffect(() => {
    if (!currentProject?.assigneeData) return;

    const existingEmails: string[] = [];
    const assigneesNew: Assignee[] = [];

    const assigneeGroups: { type: AssigneeType; flag: AccessFlag }[] = [
      { type: 'peopleAssignees', flag: 'isPeopleChecked' },
      { type: 'hlReelAssignees', flag: 'isHlReelChecked' },
      { type: 'clipsAssignees', flag: 'isClipsChecked' },
    ];

    assigneeGroups.forEach((group) => {
      currentProject.assigneeData?.[group.type]?.forEach(
        (assigneeEmail: string) => {
          if (!existingEmails.includes(assigneeEmail))
            existingEmails.push(assigneeEmail);

          const foundAssignee = assigneesNew.find(
            (assignee: Assignee) => assignee.email === assigneeEmail,
          );

          if (foundAssignee) {
            foundAssignee[group.flag] = true;
            if (
              foundAssignee.isPeopleChecked &&
              foundAssignee.isHlReelChecked &&
              foundAssignee.isClipsChecked
            )
              foundAssignee.access = 'full';
          } else
            assigneesNew.push({
              email: assigneeEmail,
              access: 'limited',
              [group.flag]: true,
            });
        },
      );
    });

    setExistingEmails(existingEmails);
    setAssignees(assigneesNew);
  }, [currentProject?.assigneeData]);

  if (!currentProject) return;

  const handleAddAssignee = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trackVBAssignModalAddAssignee();

    let { emailErr } = validateEmail(email);

    assignees.forEach((assignee) => {
      if (email === assignee.email)
        emailErr = 'This email address is already added';
    });

    if (emailErr) {
      setEmailErr(emailErr);
      return;
    }

    const assignee = {
      email,
      access: 'full',
      isPeopleChecked: true,
      isHlReelChecked: true,
      isClipsChecked: true,
    };

    setAssignees([...assignees, assignee]);
    setEmail('');
  };

  const onChangeAssignee = (index: number, e: any) => {
    const assigneesTemp = [...assignees];

    const { type, name, value, checked } = e.target;

    if (name === 'access') trackVBAssignModalChangeAssess(value);

    assigneesTemp[index] = {
      ...assigneesTemp[index],
      [name]: type === 'checkbox' ? checked : value,
    };

    setAssignees(assigneesTemp);
    setErrors([]);
  };

  const handleRemoveAssignee = (index: number) => {
    trackVBAssignModalRemoveAssignee();

    const assigneesTemp = [...assignees];
    assigneesTemp.splice(index, 1);
    setAssignees(assigneesTemp);
  };

  const handleSave = async () => {
    let peopleAssignees: string[] = [];
    let hlReelAssignees: string[] = [];
    let clipsAssignees: string[] = [];
    let errorsTemp: string[] = [];

    assignees.forEach((assignee) => {
      const { isPeopleChecked, isHlReelChecked, isClipsChecked } = assignee;
      const email = assignee.email.trim();
      const isFull = assignee.access === 'full';

      if (isFull || isPeopleChecked) peopleAssignees.push(email);
      if (isFull || isHlReelChecked) hlReelAssignees.push(email);
      if (isFull || isClipsChecked) clipsAssignees.push(email);

      if (!isPeopleChecked && !isHlReelChecked && !isClipsChecked)
        errorsTemp.push(
          'No access has been granted. Please either assign some permissions or remove the assignee.',
        );
      else errorsTemp.push('');
    });

    if (errorsTemp.every((err) => err === '')) errorsTemp = [];

    if (!isEmpty(errorsTemp)) {
      setErrors(errorsTemp);
      return;
    }

    try {
      setIsSaving(true);

      const assigneeData = { peopleAssignees, hlReelAssignees, clipsAssignees };
      await updateEvent(currentProject.eventId, { assigneeData });

      assignees.forEach(async (assignee) => {
        // if email already existed, it already received email
        if (existingEmails.includes(assignee.email)) return;

        const emailData = {
          recipientEmail: assignee.email,
          templateId: EMAILS.ASSIGNEE_INVITE,
          dynamicTemplateData: {
            name: user.firstName ?? user.email,
            event_name: currentProject.name,
            share_url: `${window.location.origin}/${currentProject.eventId}${URL.VB_ASSIGNEE}?email=${assignee.email}`,
          },
        };

        const isSuccess = await sendEmail(emailData);

        if (!isSuccess)
          toast.error(
            <div>
              Email couldn't be sent to <b>{assignee.email}</b>, please try
              again later.
            </div>,
          );
      });

      await updateCurrentEvent();

      toast.success(
        'Permissions have been updated, and emails have been sent to the assignees.',
      );
      trackVBAssignModalSaveSuccess();
    } catch {
      toast.error('Something went wrong. Try again later');
      trackVBAssignModalSaveError();
    } finally {
      setIsSaving(false);
    }
  };

  const accessOptions = [
    {
      value: 'full',
      label: 'Full Access',
      description:
        'Assignees can access people, highlight reels, and clips upload pages.',
    },
    {
      value: 'limited',
      label: 'Limited Access',
      description:
        'Assignees will only have access to the upload pages you’ve selected.',
    },
  ];

  const accessCheckboxes: { name: AccessFlag; label: string }[] = [
    { name: 'isPeopleChecked', label: 'People' },
    { name: 'isHlReelChecked', label: 'Highlight Reel' },
    { name: 'isClipsChecked', label: 'Clips' },
  ];

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Body>
        <h5>Assign to people</h5>

        <Form onSubmit={handleAddAssignee}>
          <div className="flex gap-2">
            <FloatingLabel
              label="Enter assignee email"
              type="email"
              name="email"
              value={email ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                setEmailErr(null);
              }}
              isInvalid={!!emailErr}
              errMsg={emailErr ?? ''}
              required
              className="w-3/4"
            />

            <Button colour="grey" type="submit" className="w-1/4">
              Add
            </Button>
          </div>
        </Form>

        {!isEmpty(assignees) && (
          <div className="flex flex-col gap-4">
            {assignees.map((assignee, index) => (
              <div
                key={assignee.email}
                className="flex flex-col gap-1 sm:gap-0"
              >
                <div className="flex items-center gap-2">
                  <UserCircle weight="duotone" size="1.5rem" />

                  <div>{assignee.email}</div>

                  <Dropdown
                    btnData={{
                      label: `${assignee.access} Access`,
                      variant: 'primary-thin',
                      colour: 'custom',
                      withCaret: true,
                      className: 'ml-auto text-sm capitalize',
                    }}
                    menuItems={[
                      ...accessOptions.map((option) => {
                        const content = (
                          <>
                            <div
                              className={classNames('flex items-center gap-2', {
                                'text-success-900 font-bold':
                                  option.value === assignee.access,
                              })}
                            >
                              {option.value === assignee.access ? (
                                <Check weight="bold" />
                              ) : (
                                <div className="w-4" />
                              )}
                              <div>{option.label}</div>
                            </div>

                            <div className="pl-6 text-xs text-wrap opacity-50">
                              {option.description}
                            </div>
                          </>
                        );

                        return {
                          customContent: content,
                          onClick: () =>
                            onChangeAssignee(index, {
                              target: { name: 'access', value: option.value },
                            }),
                          closeOnClick: true,
                          className: 'w-80 flex-col items-start px-3 text-sm',
                        };
                      }),
                      { type: 'divider' },
                      {
                        customContent: (
                          <div className="pl-6 text-sm text-error-900">
                            Remove
                          </div>
                        ),
                        onClick: () => handleRemoveAssignee(index),
                      },
                    ]}
                    position="bottom-right"
                  />
                </div>

                {assignee.access === 'limited' && (
                  <div className="flex gap-4 text-sm">
                    {accessCheckboxes.map((check) => (
                      <Checkbox
                        id={`${check.label.toLowerCase().replace(/\s+/g, '-')}-checkbox-${index}`}
                        label={check.label}
                        name={check.name}
                        checked={assignee[check.name] ?? false}
                        onChange={(e) => onChangeAssignee(index, e)}
                      />
                    ))}
                  </div>
                )}

                <FormError errMsg={errors[index]} />
              </div>
            ))}
          </div>
        )}

        <div className="text-sm italic">
          Your assignees will get an email with a link, giving them access only
          to the upload sections you’ve designated for them.
        </div>

        <hr className="my-1" />

        <h5>Share a link instead</h5>

        <LinkBox
          link={`${window.location.origin}/${currentProject.eventId}${URL.VB_ASSIGNEE}?accessCode=${moment(currentProject.created).format('x')}`}
          handleOpenTrack={trackVBAssignModalOpenLink}
          handleCopyTrack={trackVBAssignModalCopyLink}
        />

        <div className="text-sm italic">
          Anyone with this link will have full access.
        </div>

        <Button
          onClick={handleSave}
          disabled={!isEmpty(errors)}
          isSubmitBtn
          isProcessing={isSaving}
        >
          Save
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default AssignModal;
